import { useEffect, useState } from 'react';
import { useT } from '../i18n';

const STORAGE_KEY = 'cradle7.tutorialSeen';
const AUTO_DISMISS_MS = 6000;

function hasSeenTutorial(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, '1');
    }
  } catch {
    /* ignore */
  }
}

/**
 * One-time onboarding overlay shown above the Phaser canvas on the player's
 * first run. Dismissable by tap or auto-fades after 6 seconds.
 */
export function Tutorial() {
  const { t } = useT();
  const [visible, setVisible] = useState(() => !hasSeenTutorial());

  // Auto-dismiss after AUTO_DISMISS_MS
  useEffect(() => {
    if (!visible) return;
    const tm = setTimeout(() => {
      markSeen();
      setVisible(false);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(tm);
  }, [visible]);

  if (!visible) return null;

  const onDismiss = () => {
    markSeen();
    setVisible(false);
  };

  return (
    <div
      onPointerDown={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 16, 0.55)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-6)',
        padding: 'var(--space-6)',
        zIndex: 8, /* above HUD (5), below PerkChoice (10) and EndScreen (20) */
        cursor: 'pointer',
      }}
    >
      <div className="label glow-secondary fade-in" style={{ color: 'var(--fg-secondary)' }}>
        {t('tutorial.label')}
      </div>

      <div className="headline pulse glow-primary fade-in" style={{ fontSize: 'clamp(1.4rem, 6vw, 2.5rem)', textAlign: 'center' }}>
        {t('tutorial.title')}
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        width: '100%',
        maxWidth: '20rem',
      }}>
        <TutorialHint icon="✋" text={t('tutorial.hint.move')} delay={0} />
        <TutorialHint icon="⚡" text={t('tutorial.hint.fire')} delay={120} />
        <TutorialHint icon="◆" text={t('tutorial.hint.extract')} delay={240} />
        <TutorialHint icon="🚪" text={t('tutorial.hint.door')} delay={360} />
      </div>

      <div className="label blink" style={{ color: 'var(--fg-muted)', marginTop: 'var(--space-4)' }}>
        {t('tutorial.dismiss')}
      </div>
    </div>
  );
}

function TutorialHint({
  icon,
  text,
  delay,
}: {
  icon: string;
  text: string;
  delay: number;
}) {
  return (
    <div
      className="slide-in-up"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        border: '1px solid var(--fg-primary-dim)',
        background: 'rgba(0, 8, 32, 0.7)',
        animationDelay: `${delay}ms`,
        animationFillMode: 'both',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: '1.6rem',
          width: 40,
          textAlign: 'center',
          flex: '0 0 auto',
          filter: 'drop-shadow(0 0 6px var(--fg-primary))',
        }}
      >
        {icon}
      </span>
      <span style={{
        fontSize: '0.85rem',
        color: 'var(--fg-primary)',
        textShadow: '0 0 4px #000',
      }}>
        {text}
      </span>
    </div>
  );
}
